import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { PAGAMENTOS_INSCRICAO_FILTRADOS_LIGA } from '../../../services/financeiroService';
import { formatCurrency } from '../../../utils/money';
import type { QuebraFinanceira } from '../../../types/financeiro';
import { OrigemConfirmacaoBadge } from '../../../components/financeiro';

// Filtros de categoria, atleta, status e período que DISPARAM NOVA CONSULTA ao mudar — nunca
// filtram em memória sobre a página já carregada (AC-A3). `quebra` (mesmo campo já devolvido por
// useFinanceiroLiga) popula o filtro de Categoria com nomes reais, sem uma query própria só para
// listar categorias (RecebimentosLigaTab.test.tsx, ponto 1 do cabeçalho).

export interface RecebimentosLigaTabProps {
  ligaId: string;
  quebra: QuebraFinanceira[];
}

// Shape real de PagamentoInscricao (ver services/financeiroService.ts,
// PAGAMENTOS_INSCRICAO_FILTRADOS_LIGA → pagamentosInscricaoFiltrados,
// inplay-back/src/main/resources/graphql/pagamentoInscricao.graphqls:1-19). Cada pagamento pode
// cobrir várias inscrições (equipe de duplas); a lista de nomes de atleta e a categoria são
// derivadas de `inscricoes`, mesmo padrão de payments/LigaRecebimentosTab.tsx:339-357 (a aba que
// este hub substitui).
interface PagamentoRecebimentoLigaItem {
  id: string;
  valor: number;
  status: string;
  origemConfirmacao: 'WEBHOOK' | 'MANUAL' | null;
  dataPagamento: string | null;
  criadoEm: string | null;
  inscricoes:
    | ({
        id: string;
        categoriaTorneio: { id: string; nome: string } | null;
        equipe: {
          atletas: ({ id: string; usuario: { nome: string } | null } | null)[] | null;
        } | null;
      } | null)[]
    | null;
}

interface PagamentosInscricaoFiltradosLigaData {
  pagamentosInscricaoFiltrados: {
    items: PagamentoRecebimentoLigaItem[] | null;
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
  } | null;
}

interface PagamentosInscricaoFiltradosLigaVars {
  ligaId: string;
  categoriaTorneioId?: string;
  status?: string;
  inicio?: string;
  fim?: string;
  nomeAtleta?: string;
  page: number;
  size: number;
}

const STATUS_OPTIONS = ['PAGO', 'PENDENTE', 'CANCELADO', 'ESTORNADO'];

// Deriva "Categoria — Atleta1, Atleta2" a partir de `inscricoes` (mesmo padrão de
// payments/LigaRecebimentosTab.tsx:158-213, renderCategoriaLinks/renderUsuarioLinks — aqui sem
// links, só texto, porque esta aba ainda não navega para a inscrição).
function descricaoInscricoes(inscricoes: PagamentoRecebimentoLigaItem['inscricoes']): string {
  if (!inscricoes || inscricoes.length === 0) return '-';
  const categorias = new Set<string>();
  const atletas = new Set<string>();
  inscricoes.forEach((ins) => {
    if (ins?.categoriaTorneio?.nome) categorias.add(ins.categoriaTorneio.nome);
    ins?.equipe?.atletas?.forEach((a) => {
      if (a?.usuario?.nome) atletas.add(a.usuario.nome);
    });
  });
  const categoriaLabel = Array.from(categorias).join(', ') || '-';
  const atletaLabel = Array.from(atletas).join(', ');
  return atletaLabel ? `${categoriaLabel} — ${atletaLabel}` : categoriaLabel;
}

const RecebimentosLigaTab: React.FC<RecebimentosLigaTabProps> = ({ ligaId, quebra }) => {
  const [categoriaTorneioId, setCategoriaTorneioId] = useState('');
  const [status, setStatus] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [atleta, setAtleta] = useState('');

  const { data, loading, error } = useQuery<
    PagamentosInscricaoFiltradosLigaData,
    PagamentosInscricaoFiltradosLigaVars
  >(PAGAMENTOS_INSCRICAO_FILTRADOS_LIGA, {
    variables: {
      ligaId,
      categoriaTorneioId: categoriaTorneioId || undefined,
      status: status || undefined,
      inicio: inicio || undefined,
      fim: fim || undefined,
      nomeAtleta: atleta || undefined,
      page: 0,
      size: 10,
    },
    fetchPolicy: 'no-cache',
  });

  // pagamentosInscricaoFiltrados tem argumento `nomeAtleta` real (Phase 1 desta PRD) — mudar o
  // filtro atualiza a variável `nomeAtleta` acima, e o `useQuery` já dispara uma nova consulta de
  // rede sozinho quando `variables` muda (AC-A3): nunca um filtro em memória sobre os itens já
  // carregados. Um `refetch()` explícito aqui seria redundante e podia disparar 2 requisições.
  const handleAtletaChange = (value: string) => {
    setAtleta(value);
  };

  const items = data?.pagamentosInscricaoFiltrados?.items ?? [];
  const categoriasComNome = quebra.filter((linha) => !!linha.categoriaTorneioId);

  return (
    <div>
      <div className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <div className="d-flex flex-wrap gap-3 align-items-end">
          <div>
            <label htmlFor="recebimentos-liga-categoria" className="d-block font-xsss fw-600 mb-1">
              Categoria
            </label>
            <select
              id="recebimentos-liga-categoria"
              className="form-select form-select-sm"
              value={categoriaTorneioId}
              onChange={(e) => setCategoriaTorneioId(e.target.value)}
            >
              <option value="">Todas</option>
              {categoriasComNome.map((linha) => (
                <option key={linha.categoriaTorneioId as string} value={linha.categoriaTorneioId as string}>
                  {linha.categoriaNome ?? linha.categoriaTorneioId}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="recebimentos-liga-status" className="d-block font-xsss fw-600 mb-1">
              Status
            </label>
            <select
              id="recebimentos-liga-status"
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Todos</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="recebimentos-liga-atleta" className="d-block font-xsss fw-600 mb-1">
              Atleta
            </label>
            <input
              id="recebimentos-liga-atleta"
              type="text"
              className="form-control form-control-sm"
              value={atleta}
              onChange={(e) => handleAtletaChange(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="recebimentos-liga-inicio" className="d-block font-xsss fw-600 mb-1">
              Data Início
            </label>
            <input
              id="recebimentos-liga-inicio"
              type="date"
              className="form-control form-control-sm"
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="recebimentos-liga-fim" className="d-block font-xsss fw-600 mb-1">
              Data Fim
            </label>
            <input
              id="recebimentos-liga-fim"
              type="date"
              className="form-control form-control-sm"
              value={fim}
              onChange={(e) => setFim(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          Erro ao carregar recebimentos. Tente novamente.
        </div>
      ) : loading ? (
        <div className="text-center py-4">Carregando recebimentos...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-4">Nenhum recebimento encontrado.</div>
      ) : (
        <ul className="list-unstyled mb-0">
          {items.map((item) => (
            <li key={item.id} className="d-flex justify-content-between border-bottom py-2">
              <span>
                {descricaoInscricoes(item.inscricoes)} — {formatCurrency(item.valor)}
              </span>
              <OrigemConfirmacaoBadge status={item.origemConfirmacao} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RecebimentosLigaTab;
